// Provider products — which verified provider code fulfils each product on each
// network (Selcom airtime: utility code "TOP", from Selcom's Utility Codes
// table). A provider that needs codes sells nothing on a network without an
// ENABLED mapping, and enabling needs a citation of the provider's own
// confirmation. Codes are never typed into the employee's browser.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Button, Card, Checkbox, Empty, Form, Input, InputNumber, Modal, Select, Skeleton, Space, Switch, Table, Tag,
  Typography, message,
} from 'antd';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import * as api from '../../services/commerceOpsService';
import {
  COUNTRY, MAPPABLE_PRODUCTS, NETWORKS, apiErrorMessage, expandNetworks, formatMinor, productLabel,
  providerProductError, type ProviderInfo, type ProviderProduct, type ProviderProductInput,
} from './everydayServicesLogic';

const { Text } = Typography;

type Load =
  | { state: 'loading' }
  | { state: 'error'; error: string }
  | { state: 'ready'; items: ProviderProduct[]; providers: ProviderInfo[] };

const networkLabel = (code: string) => NETWORKS.find((n) => n.code === code)?.label ?? code;

const EMPTY: ProviderProductInput = {
  provider_code: '', country_code: COUNTRY, product_type: 'AIRTIME', operator_code: '', provider_product_code: '',
  display_name: '', price_minor: null, validity: '', volume: '', enabled: false, disabled_reason: '', verified_ref: '',
};

export const ProviderProductsTab: React.FC<{ canEdit: boolean }> = ({ canEdit }) => {
  const [load, setLoad] = useState<Load>({ state: 'loading' });
  const [editing, setEditing] = useState<ProviderProductInput | null>(null);
  const [networks, setNetworks] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  const reload = useCallback(async () => {
    setLoad((l) => (l.state === 'ready' ? l : { state: 'loading' }));
    try {
      const r = await api.listProviderProducts();
      setLoad({ state: 'ready', ...r });
    } catch (e) {
      setLoad({ state: 'error', error: apiErrorMessage(e, 'Request failed') });
    }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const providers = load.state === 'ready' ? load.providers : [];
  const provider = providers.find((p) => p.code === editing?.provider_code);
  const formError = editing ? (networks.length === 0 ? 'Choose at least one network.' : providerProductError({ ...editing, operator_code: networks[0] }, provider)) : null;

  const openNew = () => {
    setEditing({ ...EMPTY, provider_code: providers.find((p) => p.code === 'selcom')?.code ?? providers[0]?.code ?? '' });
    setNetworks([]);
  };
  const openEdit = (r: ProviderProduct) => {
    setEditing({
      id: r.id, provider_code: r.provider_code, country_code: r.country_code, product_type: r.product_type,
      operator_code: r.operator_code, provider_product_code: r.provider_product_code, display_name: r.display_name,
      price_minor: r.price_minor ?? null, validity: r.validity, volume: r.volume, enabled: r.enabled,
      disabled_reason: r.disabled_reason, verified_ref: r.verified_ref,
    });
    setNetworks([r.operator_code]);
  };

  const save = async () => {
    if (!editing || formError) return;
    setSaving(true);
    const done: string[] = [];
    try {
      for (const body of expandNetworks(editing, networks)) {
        await api.upsertProviderProduct(body);
        done.push(networkLabel(body.operator_code));
      }
      message.success(`Saved for ${done.join(', ')}`);
      setEditing(null);
      reload();
    } catch (e) {
      // Earlier networks may already be saved — say exactly which.
      message.error(`${done.length ? `Saved for ${done.join(', ')}; then: ` : ''}${apiErrorMessage(e, 'Could not save the mapping')}`, 8);
      reload();
    } finally {
      setSaving(false);
    }
  };

  const columns = useMemo(() => [
    { title: 'Provider', dataIndex: 'provider_code', render: (v: string) => <Tag>{v}</Tag> },
    { title: 'Product', dataIndex: 'product_type', render: (v: string) => productLabel(v) },
    { title: 'Network', dataIndex: 'operator_code', render: networkLabel },
    { title: 'Provider code', dataIndex: 'provider_product_code', render: (v: string) => <Text code>{v}</Text> },
    {
      title: 'Price', dataIndex: 'price_minor',
      render: (v: number | null | undefined, r: ProviderProduct) => (v == null ? <Text type="secondary">Open amount</Text> : formatMinor(v, 'TZS') + (r.validity ? ` · ${r.validity}` : '')),
    },
    {
      title: 'Status', dataIndex: 'enabled',
      render: (on: boolean, r: ProviderProduct) => (on
        ? <Tag color="green">Selling</Tag>
        : <Space direction="vertical" size={0}><Tag>Off</Tag><Text type="secondary" style={{ fontSize: 12 }}>{r.disabled_reason}</Text></Space>),
    },
    { title: 'Confirmed by', dataIndex: 'verified_ref', render: (v: string) => v || <Text type="secondary">—</Text> },
    { title: 'Version', dataIndex: 'mapping_version' },
    ...(canEdit ? [{ title: '', key: 'edit', render: (_: unknown, r: ProviderProduct) => <Button size="small" onClick={() => openEdit(r)}>Edit</Button> }] : []),
  ], [canEdit]);

  if (load.state === 'loading') return <Card size="small"><Skeleton active paragraph={{ rows: 5 }} /></Card>;
  if (load.state === 'error') {
    return (
      <Alert type="error" showIcon message="Couldn't load provider products — what can be sold is not known."
        description={load.error} action={<Button onClick={reload}>Retry</Button>} />
    );
  }

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Card size="small" title="Providers on this environment" extra={<Button icon={<ReloadOutlined />} onClick={reload}>Refresh</Button>}>
        {providers.length === 0
          ? <Empty description="No provider is configured on this environment." />
          : (
            <Space direction="vertical" style={{ width: '100%' }}>
              {providers.map((p) => (
                <div key={p.code}>
                  <Space wrap>
                    <Tag color={p.product_types.length ? 'green' : 'default'}>{p.code}</Tag>
                    {p.product_types.length
                      ? <Text>Can sell: {p.product_types.map(productLabel).join(', ')}</Text>
                      : <Text type="warning">Sells nothing</Text>}
                    <Text type="secondary">Callbacks: {p.callbacks ? 'on' : 'off (status checks only)'}</Text>
                  </Space>
                  {p.disabled_reason && <div><Text type="secondary">{p.disabled_reason}</Text></div>}
                </div>
              ))}
            </Space>
          )}
      </Card>

      <Card size="small" title="Product mappings"
        extra={canEdit && <Button type="primary" icon={<PlusOutlined />} onClick={openNew} disabled={providers.length === 0}>Add mapping</Button>}>
        <Alert type="info" showIcon className="mb-3"
          message="A provider that needs codes sells nothing on a network without an enabled mapping here. Selcom prepaid airtime uses utility code TOP for every network (Selcom API docs → Utility Codes). Data bundles need Selcom's SKU list." />
        <Table rowKey="id" size="small" columns={columns as any} dataSource={load.items} pagination={false}
          locale={{ emptyText: 'No mappings yet — nothing that needs a code can be sold.' }} scroll={{ x: true }} />
      </Card>

      <Modal open={!!editing} title="Provider product mapping" onCancel={() => setEditing(null)} onOk={save}
        okText="Save" okButtonProps={{ disabled: !!formError, loading: saving }} destroyOnClose width={560}>
        {editing && (
          <Form layout="vertical">
            {editing.id && (
              <Alert className="mb-3" type="info" showIcon
                message="Provider, product, network and code are fixed for an existing mapping. To use a different code, add a new mapping and switch this one off." />
            )}
            <Form.Item label="Provider">
              <Select disabled={!!editing.id} value={editing.provider_code} onChange={(v) => setEditing({ ...editing, provider_code: v })}
                options={providers.map((p) => ({ value: p.code, label: p.code }))} />
            </Form.Item>
            <Form.Item label="Product">
              <Select disabled={!!editing.id} value={editing.product_type} onChange={(v) => setEditing({ ...editing, product_type: v })}
                options={MAPPABLE_PRODUCTS.map((p) => ({ value: p, label: productLabel(p) }))} />
            </Form.Item>
            <Form.Item label="Networks" extra="The same code is saved for each network you tick.">
              <Checkbox.Group disabled={!!editing.id} value={networks} onChange={(v) => setNetworks(v as string[])}
                options={NETWORKS.map((n) => ({ value: n.code, label: n.label }))} />
            </Form.Item>
            <Form.Item label="Provider code" extra="Selcom prepaid airtime: TOP">
              <Input disabled={!!editing.id} value={editing.provider_product_code} maxLength={40}
                onChange={(e) => setEditing({ ...editing, provider_product_code: e.target.value.trim() })} />
            </Form.Item>
            <Form.Item label="Name shown to employees">
              <Input value={editing.display_name} maxLength={80} onChange={(e) => setEditing({ ...editing, display_name: e.target.value })} />
            </Form.Item>
            <Space wrap>
              <Form.Item label="Fixed price (TZS)" extra="Blank = open amount (airtime)">
                <InputNumber min={1} value={editing.price_minor ?? undefined}
                  onChange={(v) => setEditing({ ...editing, price_minor: v == null ? null : Number(v) })} />
              </Form.Item>
              <Form.Item label="Validity"><Input value={editing.validity} placeholder="7 days" onChange={(e) => setEditing({ ...editing, validity: e.target.value })} /></Form.Item>
              <Form.Item label="Volume"><Input value={editing.volume} placeholder="3 GB" onChange={(e) => setEditing({ ...editing, volume: e.target.value })} /></Form.Item>
            </Space>
            <Form.Item label="Provider's confirmation" extra="Doc section, email or ticket that confirms this code — required to enable.">
              <Input value={editing.verified_ref} maxLength={200} onChange={(e) => setEditing({ ...editing, verified_ref: e.target.value })} />
            </Form.Item>
            <Form.Item label="Selling">
              <Switch checked={editing.enabled} onChange={(v) => setEditing({ ...editing, enabled: v })} />
            </Form.Item>
            {!editing.enabled && (
              <Form.Item label="Why it's off (shown to operators)">
                <Input value={editing.disabled_reason} maxLength={200} onChange={(e) => setEditing({ ...editing, disabled_reason: e.target.value })} />
              </Form.Item>
            )}
            {formError && <Alert type="warning" showIcon message={formError} />}
            <Alert className="mt-3" type="info" showIcon
              message="Changing the code or price creates a new version — purchases already made keep the version they were sold on." />
          </Form>
        )}
      </Modal>
    </Space>
  );
};

export default ProviderProductsTab;
